import React from 'react';
import { Shield, Heart, Phone, ArrowRight, CheckCircle, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';
import FAQRichSnippet from '../../components/FAQRichSnippet';

const VETERAN_FAQS = [
  { q: "Does SDORA Wellbeing specialize in veteran care?", a: "Yes. We provide culturally informed mental health care that respects the unique experiences of military service. Our clinicians understand combat-related trauma, PTSD, reintegration challenges, and the specific needs of veterans and their families." },
  { q: "Can I use my VA benefits at SDORA Wellbeing?", a: "SDORA Wellbeing is a VACCN (Veterans Affairs Community Care Network) provider. We can help you navigate your VA benefits and determine eligibility for community care services. Contact us for details." },
  { q: "What services are available for veterans?", a: "We offer individual therapy, PTSD treatment, medication management, psychiatric rehabilitation, case management, and community reintegration support. Telehealth is also available for eligible veterans." },
  { q: "Do I need a VA referral to be seen?", a: "It depends on your VA coverage. Some veterans can self-refer through Community Care, while others may need a referral from their VA provider. Call us and we will help you determine the right process." },
];

const VeteranServicesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Veteran Mental Health Services"
        description="Mental health and behavioral health services for veterans at SDORA Wellbeing in Pikesville, MD. Therapy, psychiatric rehabilitation, medication management, and community support. Accepting Medicaid and most insurance."
        path="/services/veteran-services"
      />
      <Navigation />

      {/* Hero */}
      <div className="bg-gradient-to-br from-slate-700 to-slate-900 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-12 h-12 mx-auto mb-4 opacity-80" />
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">Veteran Mental Health Services</h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            Compassionate, culturally responsive mental health care for those who have served. You gave your best — let us support your well-being.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Overview */}
        <section className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Supporting Veterans and Their Families</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            SDORA Wellbeing is proud to serve veterans, active-duty service members, and military families. 
            We understand that the transition to civilian life, combat exposure, military sexual trauma, and other service-related 
            experiences can affect mental health in profound ways.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Our clinical team provides evidence-based, trauma-informed care tailored to the unique challenges 
            veterans face — including PTSD, depression, anxiety, substance use, adjustment difficulties, and family relationship stress.
          </p>
        </section>

        {/* Services */}
        <section className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Services Available to Veterans</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              ["Individual Therapy", "Trauma-informed therapy including CBT, CPT, and EMDR where clinically appropriate"],
              ["Psychiatric Rehabilitation (PRP)", "Skill-building, community integration, and functional recovery support"],
              ["Medication Management", "Psychiatric evaluation and ongoing medication coordination"],
              ["Case Management", "Navigating benefits, housing, employment, and community resources"],
              ["Family Therapy", "Supporting relationships affected by service-related stress"],
              ["Telehealth", "Virtual sessions for veterans who prefer remote access to care"],
            ].map(([title, desc]) => (
              <div key={title} className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                <h3 className="text-sm font-bold text-gray-900 mb-1">{title}</h3>
                <p className="text-xs text-gray-600">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Important Note */}
        <section className="mb-12 bg-amber-50 rounded-xl p-6 border border-amber-200">
          <h2 className="text-base font-bold text-gray-900 mb-2">Important Information</h2>
          <p className="text-sm text-gray-700 leading-relaxed">
            SDORA Wellbeing is not affiliated with or authorized by the U.S. Department of Veterans Affairs (VA). 
            We accept Medicaid, Medicare, and most commercial insurance plans. Veterans who need VA-specific services or 
            benefits should contact the VA directly. We are happy to coordinate care with your VA providers when applicable.
          </p>
        </section>

        {/* CTA */}
        <section className="text-center bg-slate-50 rounded-xl p-8 border border-slate-200">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Ready to Get Started?</h2>
          <p className="text-sm text-gray-600 mb-4">Contact us to discuss how we can support your mental health and well-being.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="tel:+18777725150" className="inline-flex items-center gap-2 bg-slate-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-slate-800 transition-colors">
              <Phone className="w-4 h-4" /> (877) 772-5150
            </a>
            <Link to="/get-started" className="inline-flex items-center gap-2 bg-white text-slate-700 border border-slate-300 px-6 py-3 rounded-lg font-semibold hover:bg-slate-50 transition-colors">
              Get Started <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
        <FAQRichSnippet faqs={VETERAN_FAQS} />
      </div>

      <Footer />
    </div>
  );
};

export default VeteranServicesPage;
